import gsap from 'gsap';
import {
    BufferGeometry, CylinderBufferGeometry, Euler, Group, Mesh, Object3D, PlaneBufferGeometry, Points, Vector3
} from 'three';
import { Geometry } from 'three/examples/jsm/deprecated/Geometry';
import { objectsLib } from '../../../../data/settings';
import { GeometriesFactory } from '../factories/GeometriesFactory';
import { MaterialsFactory } from '../factories/MaterialsFactory';
import { UNIT } from '../../../constants/sizes';
import { mergeBufferGeometries } from '../../../../vendors/three.examples/BufferGeometryUtils';
import { mathSign } from '../../../helpers/sign';
// import { FlagMeshMatcapMaterial } from '../materials/FlagMeshMatcapMaterial';

const setMaterial = (obj, material) => {
    if (obj instanceof Mesh || obj instanceof Points) {
        obj.material = material;
    }

    const { children } = obj;

    if (!children || children.length < 1) return;

    children.forEach((chn) => setMaterial(chn, material));
};

const ease = 'power2.out';

export class DecorObject3d extends Group {
    #obj3d;
    #objG;
    #objIdleG;
    #map;
    #materialToPassTime;
    #positionOnAxis = new Vector3();
    #positionOnAxis0 = new Vector3();
    #axisAngle = 0;
    #axisAngle0 = 0;
    #axisAngleStartDiff = Math.PI * 3.3;
    #rotationY = 0;
    #rotation = new Euler();
    #positionOnAxisChanged = true;
    #axisAngleChanged = true;
    #rotationYChanged = true;
    #rotationChanged = true;
    #lookAhead = true;
    #loop = true;
    #loopFactor = 1;

    constructor(params) {
        super();

        const {
            objectKey, map, needLoop, loopFactor, angleStartDiff, lookAhead
        } = params;
        const obj = objectsLib[objectKey];

        if (!obj) {
            console.warn(`Object settings with the key ${objectKey} has not be found.`);

            return;
        }

        const objG = new Group();
        const objIdleG = new Group();
        const { geometryName, geometryConstructorName, constructorName } = obj;
        let obj3d = null;

        if (geometryName || geometryConstructorName) {
            const { material } = obj;
            const matrl = MaterialsFactory.create(material);
            // eslint-disable-next-line no-nested-ternary
            let geom = geometryName
                ? GeometriesFactory.get(geometryName)
                : (geometryConstructorName ? GeometriesFactory.construct(geometryConstructorName) : null);

            if (!geom) return;

            if (geom instanceof Array) [geom] = geom;

            if (geom instanceof BufferGeometry || geom instanceof Geometry) {
                obj3d = new Mesh(geom, matrl);
            } else if (geom instanceof Object3D) {
                obj3d = geom;

                setMaterial(obj3d, matrl);
            }
        } else if (constructorName === 'flag') {
            const { materialSet } = obj;

            if (!materialSet || materialSet.length < 1) return;

            obj3d = new Group();

            let flagGeom = new CylinderBufferGeometry(UNIT.refSize * 0.0034, UNIT.refSize * 0.0034, UNIT.refSize * 0.14, 24);
            const geom2 = new PlaneBufferGeometry(UNIT.refSize * 0.2, UNIT.refSize * 0.14, 10, 8);

            flagGeom.translate(-UNIT.refSize * 0.0017, UNIT.refSize * 0.08, 0);
            geom2.translate(UNIT.refSize * 0.2 * 0.5, UNIT.refSize * 0.08, 0);

            flagGeom = mergeBufferGeometries([flagGeom, geom2]);

            const material1 = MaterialsFactory.create(materialSet[0]);
            let flagMesh = new Mesh(flagGeom, material1);

            obj3d.add(flagMesh);

            flagGeom = new CylinderBufferGeometry(UNIT.refSize * 0.003, UNIT.refSize * 0.003, UNIT.refSize * 0.30, 24);
            flagMesh = new Mesh(flagGeom, MaterialsFactory.create(materialSet[Math.min(1, materialSet.length - 1)]));
            flagGeom.translate(-UNIT.refSize * 0.0015, 0, 0);

            obj3d.add(flagMesh);

            this.#materialToPassTime = material1;
            // this.hasInvalidate = material1 instanceof FlagMeshMatcapMaterial;
        }

        if (!obj3d) return;

        this.hasInvalidate = true;

        objG.add(objIdleG);
        objIdleG.add(obj3d);
        this.add(objG);

        this.#objG = objG;
        this.#objIdleG = objIdleG;
        this.#obj3d = obj3d;
        this.#map = map;

        if (typeof needLoop === 'boolean') this.#loop = needLoop;
        if (typeof lookAhead === 'boolean') this.#lookAhead = lookAhead;
        if (typeof loopFactor === 'number') this.#loopFactor = loopFactor;
        if (typeof angleStartDiff === 'number') this.#axisAngleStartDiff = angleStartDiff;
    }

    update(orientation) {
        if (!this.#map) return;

        let pn = this.#map[orientation];

        if (pn && typeof pn.take === 'string') pn = this.#map[pn.take];

        if (!pn) {
            this.visible = false;

            return;
        }

        const {
            point, // X and Y
            angle,
            scale,
            rotation,
            rotationY
        } = pn;

        this.#obj3d.scale.set(scale, scale, scale);
        this.visible = true;

        this.#positionOnAxis0 = new Vector3().copy(point);
        this.#axisAngle0 = angle;
        this.axisAngle = angle;
        this.positionOnAxis = point;
        this.objRotationY = rotationY;
        this.objRotation = rotation;
    }

    invalidate(t) {
        // Flag material
        if (this.#materialToPassTime) this.#materialToPassTime.time = t;

        // Common
        if (this.#positionOnAxisChanged) {
            this.#objG.position.copy(this.#positionOnAxis);
        }
        if (this.#axisAngleChanged || this.#rotationYChanged) {
            this.rotation.y = this.#axisAngle;
            this.#objG.rotation.y = this.#rotationY - (this.#lookAhead ? this.#axisAngle : 0);
        }
        if (this.#rotationChanged) {
            this.#objIdleG.rotation.copy(this.#rotation);
        }

        this.#positionOnAxisChanged = false;
        this.#axisAngleChanged = false;
        this.#rotationYChanged = false;
        this.#rotationChanged = false;
    }

    play() {
        const tl = gsap.timeline();
        const { x, y } = this.#positionOnAxis0;
        const duration = 3;

        tl.addLabel('s', 0);
        tl.from(this.positionOnAxis, {
            x: UNIT.refSize * 3 * mathSign(x),
            y: y * 4.2,
            duration,
            ease,
            onUpdate: () => {
                this.#positionOnAxisChanged = true;
            }
        }, 's');
        tl.from(this, {
            axisAngle: this.#axisAngle0 + this.#axisAngleStartDiff,
            duration,
            ease
        }, 's');

        if (this.#loop) this.playLoop();

        this._tl = tl;
    }

    playLoop() {
        const duration = 1.2 + 0.45 * Math.random();
        const delay = 1.5 * Math.random();
        const tl = gsap.timeline({ repeat: -1, delay });
        const tl2 = gsap.timeline({ repeat: -1, delay: delay + duration * 0.25 });
        const { position } = this.#objIdleG;
        const { rotation } = this.#obj3d;
        const amp = UNIT.refSize * 0.01 * this.#loopFactor;
        const ampA = Math.PI * 0.03 * this.#loopFactor;

        tl.fromTo(position, {
            y: amp
        }, {
            y: -amp,
            duration,
            ease: 'sine.inOut'
        });
        tl.to(position, {
            y: amp,
            duration,
            ease: 'sine.inOut'
        });

        tl2.fromTo(rotation, {
            x: ampA
        }, {
            x: -ampA,
            duration,
            ease: 'sine.inOut'
        });
        tl2.to(rotation, {
            x: ampA,
            duration,
            ease: 'sine.inOut'
        });

        this._loopTls = [tl, tl2];
    }

    stop() {
        if (this._tl) this._tl.kill();
        if (this._loopTls) this._loopTls.forEach((tl) => tl.kill());
    }

    set positionOnAxis(val) {
        if (!this.#positionOnAxis.equals(val)) this.#positionOnAxisChanged = true;
        this.#positionOnAxis.copy(val);
    }

    get positionOnAxis() {
        return this.#positionOnAxis;
    }

    set axisAngle(val) {
        if (this.#axisAngle !== val) this.#axisAngleChanged = true;
        this.#axisAngle = val;
    }

    get axisAngle() {
        return this.#axisAngle;
    }

    set objRotationY(val) {
        if (this.#rotationY !== val) this.#rotationYChanged = true;
        this.#rotationY = val;
    }

    get objRotationY() {
        return this.#rotationY;
    }

    set objRotation(val) {
        if (!this.#rotation.equals(val)) this.#rotationChanged = true;
        this.#rotation.copy(val);
    }

    get objRotation() {
        return this.#rotation;
    }
}
